import { IconProps, Menu, MenuItem, IconButton, ThreeDotsIcon } from "@flat-ui";
import { useTheme } from "@mui-wrapper";
import { useAppSelector } from "@store/store-helper";
import { userStateSelector } from "@store/user/user-slice";
import { PermissionKey } from "@utils/permissions/permission-matrix";
import {
  getDeniedReason,
  hasPermission,
} from "@utils/permissions/permissions-utils";
import React, { useRef, useState } from "react";

/** Type constraint for objects that have an id or identity property */
type Identifiable = { id?: string; identity?: string };

/** Describes a single menu option for the data grid row. */
interface MenuItemOption<T extends Identifiable> {
  /** Visible text of the option */
  label: string;

  /** Icon component to render for the option */
  Icon?: (props: IconProps) => React.JSX.Element;

  /** Handler invoked when the option is activated with the item */
  onSelect?: (item: T) => void;

  /** Optional callback to get the href url of each menu item */
  getHrefUrl?: (item: T) => string;

  /** Optional predicate to determine if the item should be shown */
  shouldShow?: (item: T) => boolean;

  /** Whether the option is dangerous to apply, e.g. deleting member from workspace */
  isDangerous?: boolean;

  /** Permission the logged in user needs for the option. Without it the option is shown disabled. */
  permission?: PermissionKey;
}

interface Props<T extends Identifiable> {
  /** The item being operated on */
  item: T;

  /** Array of options with label, icon and click handler */
  menuItems: MenuItemOption<T>[];
}

/** Return the 3 dot menu with the menuItems provided */
export function DataGridOptionCell<T extends Identifiable>({
  item,
  menuItems,
}: Props<T>): React.JSX.Element {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { currentUserRoles } = useAppSelector(userStateSelector);

  function handleMenuItemSelect(menuItem: MenuItemOption<T>): void {
    menuItem.onSelect?.(item);
    setIsOpen(false);
  }

  return (
    <>
      <IconButton
        ref={buttonRef}
        aria-label="Open menu"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        size="m"
      >
        <ThreeDotsIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={() => setIsOpen(false)}
      >
        {menuItems
          .filter(({ shouldShow }) => shouldShow?.(item) ?? true)
          .map((menuItem) => {
            const href = menuItem.getHrefUrl?.(item);
            const { permission } = menuItem;
            const isDenied =
              permission !== undefined &&
              !hasPermission(currentUserRoles, permission);

            return (
              <MenuItem
                key={menuItem.label}
                label={menuItem.label}
                disabled={isDenied}
                secondaryText={
                  isDenied ? getDeniedReason(permission) : undefined
                }
                href={href}
                onClick={() => {
                  handleMenuItemSelect(menuItem);
                }}
                // Same functionality as onClick, but for middle mouse button when href is present.
                // This helps to track events when middle click is clicked
                onAuxClick={(event) => {
                  if (event.button !== 1 || !href) {
                    return;
                  }

                  handleMenuItemSelect(menuItem);
                }}
                Icon={menuItem.Icon}
                color={
                  menuItem.isDangerous
                    ? theme.vars.palette.error.main
                    : undefined
                }
              />
            );
          })}
      </Menu>
    </>
  );
}
